<script lang="ts" setup>
  import {
    Button,
    ButtonVariant,
    DatePicker,
    DROPDOWN_CONTEXT_KEY,
    DropdownApplyFooter,
    DropdownPanelScroll,
  } from '@tundr/ds-components';
  import { DatetimeFormatType, useLocale } from '@tundr/i18n';
  import { isAfter, isEqual, isWithinInterval } from 'date-fns';
  import { computed, inject, ref, watch } from 'vue';
  import { useFilters } from '../../../../composables';
  import { FilterTypes } from '../../../../utils';
  import {
    type DateRangePreset,
    getLastMonthRange,
    getLastYearRange,
  } from '../../../../utils/filters/date-range-presets.utils';
  import {
    dateColClass,
    dateRangeConstraintsClass,
    dateRangeFilterWrapperClass,
    dateRangeInputClass,
    datesRowClass,
    shortcutsRowClass,
  } from './DateRangeFilter.css';

  const { t, d } = useLocale('table');

  const props = defineProps<{
    filterNamespace: string;
    filterLabel: string;
    filterFieldName: string;
    fromKey?: string;
    toKey?: string;
    startDate?: {
      min?: Date;
      max?: Date;
    };
    endDate?: {
      min?: Date;
      max?: Date;
    };
    modelValue?: {
      startDate: Date;
      endDate: Date;
    };
  }>();

  defineOptions({
    inheritAttrs: false,
  });

  const dropdown = inject(DROPDOWN_CONTEXT_KEY, null);

  const currentFilterStore = useFilters(props.filterNamespace);

  const startDateModel = ref<Date | undefined>(props.modelValue?.startDate);
  const endDateModel = ref<Date | undefined>(props.modelValue?.endDate);

  watch(
    () => props.modelValue,
    (value) => {
      startDateModel.value = value?.startDate;
      endDateModel.value = value?.endDate;
    },
  );

  const emit = defineEmits<{
    'update:modelValue': [{ startDate?: Date; endDate?: Date }];
  }>();

  const onApplyFilter = () => {
    emit('update:modelValue', {
      startDate: startDateModel.value,
      endDate: endDateModel.value,
    });

    currentFilterStore.applyFilterValue({
      filterType: FilterTypes.DATE_RANGE,
      filterFieldName: props.filterFieldName,
      fromKey: props.fromKey,
      toKey: props.toKey,
      value: {
        startDate: startDateModel.value,
        endDate: endDateModel.value,
      },
    });

    dropdown?.close();
  };

  const startMinDate = computed(() => {
    return (props.startDate?.min as Date) || undefined;
  });
  const startMaxDate = computed(() => {
    return endDateModel.value
      ? endDateModel.value
      : (props.startDate?.max as Date) || undefined;
  });

  const endMinDate = computed(() => {
    return startDateModel.value
      ? startDateModel.value
      : (props.endDate?.min as Date) || undefined;
  });
  const endMaxDate = computed(() => {
    return (props.endDate?.max as Date) || undefined;
  });

  const startDateIsValid = computed(() => {
    return startDateModel.value && startMinDate.value && startMaxDate.value
      ? isWithinInterval(startDateModel.value, {
          start: startMinDate.value,
          end: startMaxDate.value,
        })
      : true;
  });

  const endDateIsValid = computed(() => {
    return endDateModel.value && endMinDate.value && endMaxDate.value
      ? isWithinInterval(endDateModel.value, {
          start: endMinDate.value,
          end: endMaxDate.value,
        })
      : true;
  });

  const isValidSelection = computed(() => {
    if (!startDateModel.value || !endDateModel.value) {
      return false;
    }

    return (
      startDateIsValid.value &&
      endDateIsValid.value &&
      (isAfter(endDateModel.value, startDateModel.value) ||
        isEqual(endDateModel.value, startDateModel.value))
    );
  });

  const applyPreset = ({ startDate, endDate }: DateRangePreset) => {
    startDateModel.value = startDate;
    endDateModel.value = endDate;
  };

  const onSetLastMonth = () => applyPreset(getLastMonthRange(new Date()));

  const onSetLastYear = () => applyPreset(getLastYearRange(new Date()));
</script>

<template>
  <DropdownPanelScroll :class="dateRangeFilterWrapperClass">
    <div>{{ filterLabel }}</div>
    <div :class="shortcutsRowClass">
      <Button :variant="ButtonVariant.Secondary" @click="onSetLastYear"
        >{{ t('filters.date_range.last_year') }}
      </Button>
      <Button :variant="ButtonVariant.Secondary" @click="onSetLastMonth"
        >{{ t('filters.date_range.last_month') }}
      </Button>
    </div>
    <div :class="datesRowClass">
      <div :class="dateColClass">
        <div :class="dateRangeInputClass">
          <DatePicker
            v-model="startDateModel"
            :label="t('filters.date_range.start_date.label')"
            :placeholder="t('filters.date_range.start_date.placeholder')"
            :name="`${filterFieldName}-start`"
            :error="
              startDateIsValid
                ? undefined
                : t('filters.date_range.errors.out_of_range')
            "
            required
            :teleport="true"
            :max-date="startMaxDate"
            :min-date="startMinDate"
          />
        </div>
        <div :class="dateRangeConstraintsClass">
          <span v-if="startMinDate"
            >{{ t('filters.date_range.min_date') }}:
            {{ d(startMinDate, DatetimeFormatType.SHORT_DATE) }}</span
          >
          <span v-if="startMaxDate"
            >{{ t('filters.date_range.max_date') }}:
            {{ d(startMaxDate, DatetimeFormatType.SHORT_DATE) }}</span
          >
        </div>
      </div>
      <div :class="dateColClass">
        <div :class="dateRangeInputClass">
          <DatePicker
            v-model="endDateModel"
            :label="t('filters.date_range.end_date.label')"
            :placeholder="t('filters.date_range.end_date.placeholder')"
            :name="`${filterFieldName}-end`"
            :error="
              endDateIsValid
                ? undefined
                : t('filters.date_range.errors.out_of_range')
            "
            required
            :teleport="true"
            :max-date="endMaxDate"
            :min-date="endMinDate"
          />
        </div>
        <div :class="dateRangeConstraintsClass">
          <span v-if="endMinDate"
            >{{ t('filters.date_range.min_date') }}:
            {{ d(endMinDate, DatetimeFormatType.SHORT_DATE) }}</span
          >
          <span v-if="endMaxDate"
            >{{ t('filters.date_range.max_date') }}:
            {{ d(endMaxDate, DatetimeFormatType.SHORT_DATE) }}</span
          >
        </div>
      </div>
    </div>
  </DropdownPanelScroll>

  <DropdownApplyFooter :disabled="!isValidSelection" @apply="onApplyFilter" />
</template>
